import { get, post } from "../utils/api";

export type AuthProviderKind = "telegram" | "email";

export interface LinkedProvider {
  provider: AuthProviderKind;
  verifiedAt: string | null;
  createdAt: string;
}

export interface AuthMe {
  /** users.public_id (UUID) */
  id: string;
  email: string | null;
  emailVerifiedAt: string | null;
  telegramId: number | null;
  username: string | null;
  role: string | null;
  businessId: number | null;
  linkedProviders: LinkedProvider[];
}

/**
 * Отправить OTP на email. Бэк всегда возвращает 204 (anti-enumeration),
 * кроме 429 при rate-limit.
 */
export const requestEmailCode = async (email: string): Promise<void> => {
  await post<void, { email: string }>(`/auth/email/request-code`, undefined, { email });
};

/**
 * Подтвердить OTP. На успех cookies (ow_access / ow_refresh) уже стоят,
 * в теле — профиль пользователя.
 */
export const verifyEmailCode = (email: string, code: string): Promise<AuthMe> => {
  return post<AuthMe, { email: string; code: string }>(`/auth/email/verify`, undefined, {
    email,
    code
  });
};

export const exchangeTelegram = (initData: string): Promise<AuthMe> => {
  return post<AuthMe, { initData: string }>(`/auth/telegram/exchange`, undefined, { initData });
};

export const refreshSession = (): Promise<{ ok: true }> => {
  return post<{ ok: true }>(`/auth/refresh`);
};

export const logout = async (): Promise<void> => {
  await post<void>(`/auth/logout`);
};

export const getMe = (): Promise<AuthMe> => {
  return get<AuthMe>(`/auth/me`);
};

// ── Account linking ────────────────────────────────────────────────────────

/** Запрос OTP для привязки email к уже залогиненному юзеру. */
export const requestLinkEmailCode = async (email: string): Promise<void> => {
  await post<void, { email: string }>(`/auth/link/email/request-code`, undefined, { email });
};

/** Подтверждение кода привязки email. Возвращает обновлённый профиль. */
export const verifyLinkEmail = (email: string, code: string): Promise<AuthMe> => {
  return post<AuthMe, { email: string; code: string }>(`/auth/link/email/verify`, undefined, {
    email,
    code
  });
};

/**
 * Старт привязки Telegram через бота. Возвращает готовую deep-link-ссылку
 * вида `https://t.me/<bot>?start=link_<token>` — фронту достаточно её открыть.
 * Сама привязка завершается, когда юзер нажмёт /start у бота; результат
 * приходит в чат, а в вебе обновится после `getMe()`.
 */
export const startLinkTelegram = (): Promise<{ deepLink: string; expiresAt: string }> => {
  return post<{ deepLink: string; expiresAt: string }>(`/auth/link/telegram/start`);
};
